import { useEffect, useState } from 'react';
import { LocalStorageService } from '@/shared/services/localStorage.service';
import { createChatApi } from '../api/chatApi';
import { createChatRuntime } from '../api/runtime';
import { CHAT_UNREAD_TOTAL_STORAGE_KEY } from '../config/constants';
import { resolveUnreadTotal } from '../lib/resolveUnreadTotal';
import { subscribeChatRealtime } from '../realtime/chatRealtimeBus';
import { CHAT_SOCKET_EVENT, isChatSocketEvent } from '../realtime/chatSocketTypes';

let cachedUnreadTotal = 0;
let isUnreadCacheReady = false;
let hasSyncedUnreadThisSession = false;
let unreadInitialFetchPromise: Promise<void> | null = null;

function readPersistedUnreadTotal(): number | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = sessionStorage.getItem(CHAT_UNREAD_TOTAL_STORAGE_KEY);
    if (raw == null) return null;
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 ? value : null;
  } catch {
    return null;
  }
}

function persistUnreadTotal(total: number) {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.setItem(CHAT_UNREAD_TOTAL_STORAGE_KEY, String(total));
  } catch {
    // ignore quota / private mode
  }
}

function clearPersistedUnreadTotal() {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.removeItem(CHAT_UNREAD_TOTAL_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function applyUnreadTotal(total: number) {
  cachedUnreadTotal = total;
  isUnreadCacheReady = true;
  persistUnreadTotal(total);
}

function clearUnreadCache() {
  cachedUnreadTotal = 0;
  isUnreadCacheReady = false;
  hasSyncedUnreadThisSession = false;
  unreadInitialFetchPromise = null;
  clearPersistedUnreadTotal();
}

function getInitialUnreadTotal(): number {
  const persisted = readPersistedUnreadTotal();
  if (persisted != null) {
    applyUnreadTotal(persisted);
    return persisted;
  }

  return isUnreadCacheReady ? cachedUnreadTotal : 0;
}

async function fetchUnreadTotalOnce(): Promise<void> {
  if (unreadInitialFetchPromise) {
    await unreadInitialFetchPromise;
    return;
  }

  const runtime = createChatRuntime(LocalStorageService.getAccessToken);
  const api = createChatApi(runtime);

  unreadInitialFetchPromise = api
    .getUnreadCount()
    .then((result) => {
      applyUnreadTotal(resolveUnreadTotal(result));
    })
    .catch(() => {
      // keep persisted / cached value on transient errors
    })
    .finally(() => {
      unreadInitialFetchPromise = null;
    });

  await unreadInitialFetchPromise;
}

export function useChatUnreadTotal() {
  const accessToken = LocalStorageService.getAccessToken();
  const enabled = !!accessToken;
  const [totalUnreadCount, setTotalUnreadCount] = useState(getInitialUnreadTotal);

  useEffect(() => {
    if (!enabled) {
      clearUnreadCache();
      setTotalUnreadCount(0);
      return;
    }

    if (isUnreadCacheReady) {
      setTotalUnreadCount(cachedUnreadTotal);
    }

    let disposed = false;

    const handleUnreadTotal = (total: number) => {
      applyUnreadTotal(total);
      if (!disposed) {
        setTotalUnreadCount(total);
      }
    };

    const offBus = subscribeChatRealtime((signal) => {
      if (signal.kind === 'reconnect') {
        void fetchUnreadTotalOnce().then(() => {
          if (!disposed && isUnreadCacheReady) {
            setTotalUnreadCount(cachedUnreadTotal);
          }
        });
        return;
      }

      if (signal.kind !== 'event') return;
      if (!isChatSocketEvent(signal.envelope, CHAT_SOCKET_EVENT.UnreadTotalUpdated)) {
        return;
      }

      handleUnreadTotal(resolveUnreadTotal(signal.envelope.payload));
    }, ['unread', 'reconnect']);

    void (async () => {
      if (!hasSyncedUnreadThisSession) {
        await fetchUnreadTotalOnce();
        hasSyncedUnreadThisSession = true;
      }

      if (!disposed && isUnreadCacheReady) {
        setTotalUnreadCount(cachedUnreadTotal);
      }
    })();

    return () => {
      disposed = true;
      offBus();
    };
  }, [enabled]);

  return totalUnreadCount;
}
